// WoundDetailsStep.jsx - Add search functionality (UPDATED: custom location "Other" sentinel logic)

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoArrowBack, IoCheckmarkCircle, IoSearchSharp, IoCloseCircle } from 'react-icons/io5';
import { woundTypeOptions, woundLocationMap, icd10Codes } from '../../../data/conservativeCareDashboard';
import Highlights from '../common/Highlights';

const OTHER_LOCATION_VALUE = '__OTHER__';

const WoundDetailsStep = ({
  orderStep,
  setOrderStep,
  woundDetails,
  setWoundDetails,
  ivrSubmitted,
  setOpenIvrModal,
  source,
  onComplete,
  setBillingType,
  selectedCareType,  
  billingType,      
}) => {
  const [icd10SearchTerm, setIcd10SearchTerm] = useState('');
  const [showIcd10Dropdown, setShowIcd10Dropdown] = useState(false);
  const [customLocation, setCustomLocation] = useState(false);

  const filteredIcd10Codes = useMemo(() => {
    if (!icd10SearchTerm.trim()) return icd10Codes;

    const searchLower = icd10SearchTerm.toLowerCase();
    return icd10Codes.filter(
      (item) =>
        item.code.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower)
    );
  }, [icd10SearchTerm]);

  const handleIcd10Select = (code) => {
    setWoundDetails({ ...woundDetails, icd10: code });
    setIcd10SearchTerm('');
    setShowIcd10Dropdown(false);
  };

  const updateLymphedemaMeasurements = (leg, field, value) => {
    setWoundDetails({
      ...woundDetails,
      lymphedema_measurements: {
        ...woundDetails.lymphedema_measurements,
        [leg]: {
          ...(woundDetails.lymphedema_measurements?.[leg] || {}),
          [field]: value
        }
      }
    });
  };

  const clearIcd10Selection = () => {
    setWoundDetails({ ...woundDetails, icd10: '' });
    setIcd10SearchTerm('');
  };

  const canProceedWithWoundDetails = () => {
    if (!woundDetails.type) return false;

    if (woundDetails.failed_conservative_care === null || woundDetails.failed_conservative_care === undefined) {
      return false;
    }

    if (woundDetails.failed_conservative_care === false && woundDetails.is_full_thickness === false) {
      return false;
    }
    if (selectedCareType === 'advanced' && !billingType) return false;
    return true;
  };

  const getFullThicknessWarning = () => {
    if (woundDetails.failed_conservative_care === false && woundDetails.is_full_thickness === false) {
      return (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-2xl"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-red-800 dark:text-red-300 mb-1">⚠️ Cannot Proceed</p>
              <p className="text-sm text-red-700 dark:text-red-400 leading-relaxed">
                Since the patient has <strong>not failed 4 weeks of conservative care</strong>, this wound must be{' '}
                <strong>full thickness</strong> to proceed with{' '}
                {source === 'conservative_care' ? 'Conservative Care' : 'Advanced Care'} products. Please select{' '}
                <strong>"Yes"</strong> for Full Thickness Wound.
              </p>
            </div>
          </div>
        </motion.div>
      );
    }
    return null;
  };

  return (
    <motion.div
      className="px-4 sm:px-6 lg:px-12 pb-8"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-3xl mx-auto">
        <motion.div
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-10 relative overflow-hidden"
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
        >
          <div className="relative z-10">
            <motion.button
              onClick={() => setOrderStep(1)}
              className="flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:gap-3 transition-all font-bold mb-6 group"
              whileHover={{ x: -5 }}
            >
              <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" />
              Back
            </motion.button>

            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <motion.div
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-lg ${
                        orderStep >= step
                          ? 'bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-lg'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                      }`}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: step * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {orderStep > step ? <IoCheckmarkCircle className="text-2xl" /> : step}
                    </motion.div>
                    {step < 3 && (
                      <div
                        className={`h-1 w-24 mx-2 rounded transition-all duration-500 ${
                          orderStep > step
                            ? 'bg-gradient-to-r from-blue-500 to-purple-500'
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      ></div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs font-bold text-gray-600 dark:text-gray-400">
                <span>Patient</span>
                <span className="text-blue-600 dark:text-blue-400">Wound Details</span>
                <span>IVR</span>
              </div>
            </div>

            <motion.h2
              className="text-3xl font-black text-gray-900 dark:text-white mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Step 2 of 3 - Wound Details
            </motion.h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">Provide detailed wound information</p>

            {/* Wound Type */}
            <motion.div
              className="mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <label className="block text-sm font-black text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <span className="text-2xl">🩹</span>
                <span className="uppercase tracking-wide">Wound Type</span>
              </label>
              <div className="relative">
                <select
                  value={woundDetails.type}
                  onChange={(e) => {
                    // IMPORTANT: reset location + customLocation when type changes
                    setCustomLocation(false);
                    setWoundDetails({
                      ...woundDetails,
                      type: e.target.value,
                      location: ''
                    });
                  }}
                  className="w-full px-5 py-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold appearance-none cursor-pointer hover:border-blue-400 dark:hover:border-blue-500"
                >
                  <option value="" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                    Select wound type
                  </option>
                  {woundTypeOptions.map((category) => (
                    <optgroup
                      key={category.category}
                      label={category.category}
                      className="font-bold bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white"
                    >
                      {category.options.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          className="font-medium bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {woundDetails.type && (
                <motion.div
                  className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <p className="text-sm text-blue-800 dark:text-blue-300 font-medium flex items-center gap-2">
                    <IoCheckmarkCircle className="text-blue-600 dark:text-blue-400" />
                    Selected: <span className="font-bold">{woundDetails.type}</span>
                  </p>
                </motion.div>
              )}
            </motion.div>

            {/* Wound Location */}
            {woundDetails.type && (
              <motion.div
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <label className="block text-sm font-black text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <span className="text-2xl">📍</span>
                  <span className="uppercase tracking-wide">Wound Location</span>
                </label>

                {woundLocationMap[woundDetails.type] ? (
                  <>
                    <div className="relative">
                      <select
                        value={customLocation ? OTHER_LOCATION_VALUE : woundDetails.location}
                        onChange={(e) => {
                          const val = e.target.value;

                          if (val === OTHER_LOCATION_VALUE) {
                            setCustomLocation(true);
                            setWoundDetails({ ...woundDetails, location: '' });
                          } else {
                            setCustomLocation(false);
                            setWoundDetails({ ...woundDetails, location: val });
                          }
                        }}
                        className="w-full px-5 py-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-semibold appearance-none cursor-pointer hover:border-purple-400 dark:hover:border-purple-500"
                      >
                        <option value="" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                          Select location
                        </option>

                        {woundLocationMap[woundDetails.type].map((location) => (
                          <option
                            key={location}
                            value={location}
                            className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          >
                            {location}
                          </option>
                        ))}

                        <option
                          value={OTHER_LOCATION_VALUE}
                          className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          Other / Not listed (free-text)
                        </option>
                      </select>

                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {customLocation && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-3"
                      >
                        <input
                          type="text"
                          placeholder="Enter custom wound location"
                          value={woundDetails.location}
                          onChange={(e) => setWoundDetails({ ...woundDetails, location: e.target.value })}
                          className="w-full px-5 py-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-semibold placeholder:text-gray-400"
                        />
                      </motion.div>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    placeholder="Enter wound location"
                    value={woundDetails.location}
                    onChange={(e) => setWoundDetails({ ...woundDetails, location: e.target.value })}
                    className="w-full px-5 py-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-semibold placeholder:text-gray-400"
                  />
                )}
              </motion.div>
            )}

            {/* Wound Measurements */}
            {woundDetails.type && (
              <motion.div
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <label className="block text-sm font-black text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <span className="text-2xl">📏</span>
                  <span className="uppercase tracking-wide">
                    {woundDetails.type === 'Lymphedema' ? 'Measurements' : 'Wound Measurements (cm)'}
                  </span>
                </label>

                {woundDetails.type === 'Lymphedema' ? (
                  <div className="space-y-6">
                    {/* Left Leg */}
                    <div className="border-2 border-gray-300 dark:border-gray-600 rounded-2xl p-5 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
                      <div className="flex items-center gap-2 mb-4">
                        <input
                          type="checkbox"
                          id="leftLeg"
                          checked={woundDetails.lymphedema_measurements?.leftLeg?.enabled || false}
                          onChange={(e) => updateLymphedemaMeasurements('leftLeg', 'enabled', e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300"
                        />
                        <label htmlFor="leftLeg" className="text-base font-bold text-gray-900 dark:text-white uppercase">
                          Left Leg
                        </label>
                      </div>

                      <div className="grid grid-cols-5 gap-3">
                        {['ankle', 'calf', 'thigh', 'length', 'arch'].map((field) => (
                          <div key={field}>
                            <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2">
                              {field.charAt(0).toUpperCase() + field.slice(1)}
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              disabled={!woundDetails.lymphedema_measurements?.leftLeg?.enabled}
                              value={woundDetails.lymphedema_measurements?.leftLeg?.[field] || ''}
                              onChange={(e) => updateLymphedemaMeasurements('leftLeg', field, e.target.value)}
                              className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm disabled:opacity-50"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right Leg */}
                    <div className="border-2 border-gray-300 dark:border-gray-600 rounded-2xl p-5 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
                      <div className="flex items-center gap-2 mb-4">
                        <input
                          type="checkbox"
                          id="rightLeg"
                          checked={woundDetails.lymphedema_measurements?.rightLeg?.enabled || false}
                          onChange={(e) => updateLymphedemaMeasurements('rightLeg', 'enabled', e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300"
                        />
                        <label htmlFor="rightLeg" className="text-base font-bold text-gray-900 dark:text-white uppercase">
                          Right Leg
                        </label>
                      </div>

                      <div className="grid grid-cols-5 gap-3">
                        {['ankle', 'calf', 'thigh', 'length', 'arch'].map((field) => (
                          <div key={field}>
                            <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2">
                              {field.charAt(0).toUpperCase() + field.slice(1)}
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              disabled={!woundDetails.lymphedema_measurements?.rightLeg?.enabled}
                              value={woundDetails.lymphedema_measurements?.rightLeg?.[field] || ''}
                              onChange={(e) => updateLymphedemaMeasurements('rightLeg', field, e.target.value)}
                              className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm disabled:opacity-50"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2 uppercase">
                          Length (L)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="0.0"
                          value={woundDetails.length}
                          onChange={(e) => setWoundDetails({ ...woundDetails, length: e.target.value })}
                          className="w-full px-4 py-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-semibold placeholder:text-gray-400 text-center"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2 uppercase">
                          Width (W)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="0.0"
                          value={woundDetails.width}
                          onChange={(e) => setWoundDetails({ ...woundDetails, width: e.target.value })}
                          className="w-full px-4 py-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-semibold placeholder:text-gray-400 text-center"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-2 uppercase">
                          Depth (D)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="0.0"
                          value={woundDetails.depth}
                          onChange={(e) => setWoundDetails({ ...woundDetails, depth: e.target.value })}
                          className="w-full px-4 py-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-semibold placeholder:text-gray-400 text-center"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-2">
                      * Enter measurements in centimeters (e.g., 2.5)
                    </p>
                  </>
                )}
              </motion.div>
            )}

            {/* Failed Conservative Care */}
            <motion.div
              className="mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.33 }}
            >
              <label className="block text-sm font-black text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                <span className="text-2xl">⏱️</span>
                <span className="uppercase tracking-wide">Failed 4 Weeks of Conservative Care?</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                {['Yes', 'No'].map((option, index) => (
                  <motion.label
                    key={option}
                    className={`cursor-pointer rounded-2xl p-5 border-2 text-center transition-all ${
                      (option === 'Yes' ? woundDetails.failed_conservative_care : woundDetails.failed_conservative_care === false)
                        ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 shadow-lg'
                        : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 hover:border-orange-400 dark:hover:border-orange-500'
                    }`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.37 + index * 0.1 }}
                    whileHover={{ scale: 1.03, y: -3 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <input
                      type="radio"
                      name="failedConservativeCare"
                      value={option}
                      checked={option === 'Yes' ? woundDetails.failed_conservative_care : woundDetails.failed_conservative_care === false}
                      onChange={() => setWoundDetails({ ...woundDetails, failed_conservative_care: option === 'Yes' })}
                      className="sr-only"
                    />
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-lg font-black text-gray-900 dark:text-white">{option}</span>
                      {(option === 'Yes' ? woundDetails.failed_conservative_care : woundDetails.failed_conservative_care === false) && (
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', stiffness: 200 }}
                        >
                          <IoCheckmarkCircle className="text-orange-500 text-xl" />
                        </motion.div>
                      )}
                    </div>
                  </motion.label>
                ))}
              </div>
            </motion.div>

            {/* Full Thickness */}
            <motion.div
              className="mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <label className="block text-sm font-black text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                <span className="text-2xl">🔍</span>
                <span className="uppercase tracking-wide">Full Thickness Wound?</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                {['Yes', 'No'].map((option, index) => (
                  <motion.label
                    key={option}
                    className={`cursor-pointer rounded-2xl p-5 border-2 text-center transition-all ${
                      (option === 'Yes' ? woundDetails.is_full_thickness : woundDetails.is_full_thickness === false)
                        ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 shadow-lg'
                        : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 hover:border-purple-400 dark:hover:border-purple-500'
                    }`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    whileHover={{ scale: 1.03, y: -3 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <input
                      type="radio"
                      name="fullThickness"
                      value={option}
                      checked={option === 'Yes' ? woundDetails.is_full_thickness : woundDetails.is_full_thickness === false}
                      onChange={() => setWoundDetails({ ...woundDetails, is_full_thickness: option === 'Yes' })}
                      className="sr-only"
                    />
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-lg font-black text-gray-900 dark:text-white">{option}</span>
                      {(option === 'Yes' ? woundDetails.is_full_thickness : woundDetails.is_full_thickness === false) && (
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', stiffness: 200 }}
                        >
                          <IoCheckmarkCircle className="text-purple-500 text-xl" />
                        </motion.div>
                      )}
                    </div>
                  </motion.label>
                ))}
              </div>
              {getFullThicknessWarning()}
            </motion.div>

            {/* Exudate Level */}
            <motion.div
              className="mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <label className="block text-sm font-black text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                <span className="text-2xl">💧</span>
                <span className="uppercase tracking-wide">Exudate Level</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                {['Mild', 'Moderate'].map((level, index) => (
                  <motion.label
                    key={level}
                    className={`cursor-pointer rounded-2xl p-5 border-2 text-center transition-all ${
                      woundDetails.drainage === level
                        ? 'border-cyan-500 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30 shadow-lg'
                        : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 hover:border-cyan-400 dark:hover:border-cyan-500'
                    }`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    whileHover={{ scale: 1.03, y: -3 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <input
                      type="radio"
                      name="drainage"
                      value={level}
                      checked={woundDetails.drainage === level}
                      onChange={(e) => setWoundDetails({ ...woundDetails, drainage: e.target.value })}
                      className="sr-only"
                    />
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-lg font-black text-gray-900 dark:text-white">{level}</span>
                      {woundDetails.drainage === level && (
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', stiffness: 200 }}
                        >
                          <IoCheckmarkCircle className="text-cyan-500 text-xl" />
                        </motion.div>
                      )}
                    </div>
                  </motion.label>
                ))}
              </div>
            </motion.div>

            {/* ICD-10 Code with Search */}
            <motion.div
              className="mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Highlights emoji="📋" color="teal" variant="subtle" inline className="mb-3">
                <label className="block text-sm font-black text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  ICD-10 Code
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">(Optional)</span>
                </label>
              </Highlights>

              {woundDetails.icd10 ? (
                <div className="relative">
                  <div className="w-full px-5 py-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-2 border-green-500 dark:border-green-600 rounded-2xl">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-bold text-green-800 dark:text-green-300">{woundDetails.icd10}</p>
                        <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                          {icd10Codes.find((c) => c.code === woundDetails.icd10)?.description}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={clearIcd10Selection}
                        className="flex-shrink-0 p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      >
                        <IoCloseCircle className="text-2xl" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <IoSearchSharp className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search by code or description..."
                      value={icd10SearchTerm}
                      onChange={(e) => setIcd10SearchTerm(e.target.value)}
                      onFocus={() => setShowIcd10Dropdown(true)}
                      className="w-full pl-12 pr-5 py-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all font-semibold placeholder:text-gray-400"
                    />
                  </div>

                  <AnimatePresence>
                    {showIcd10Dropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-2xl shadow-2xl max-h-80 overflow-y-auto"
                      >
                        {filteredIcd10Codes.length > 0 ? (
                          filteredIcd10Codes.map((item) => (
                            <div
                              key={item.code}
                              onClick={() => handleIcd10Select(item.code)}
                              className="px-5 py-3 hover:bg-green-50 dark:hover:bg-green-900/30 cursor-pointer transition-colors border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                            >
                              <p className="text-sm font-bold text-gray-900 dark:text-white">{item.code}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{item.description}</p>
                            </div>
                          ))
                        ) : (
                          <div className="px-5 py-8 text-center">
                            <p className="text-sm text-gray-500 dark:text-gray-400">No matching ICD-10 codes found</p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {showIcd10Dropdown && <div className="fixed inset-0 z-40" onClick={() => setShowIcd10Dropdown(false)} />}
            </motion.div>
            {selectedCareType === 'advanced' && (
              <motion.div
                className="mb-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <label className="block text-sm font-black text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <span className="text-2xl">🏦</span>
                  <span className="uppercase tracking-wide">Billing Type</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: 'promed', label: 'ProMed Billing' },
                    { value: 'provider', label: "Provider's Own Billing" },
                  ].map((opt, index) => (
                    <motion.label
                      key={opt.value}
                      className={`cursor-pointer rounded-2xl p-5 border-2 text-center transition-all ${
                        billingType === opt.value
                          ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30 shadow-lg'
                          : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 hover:border-teal-400 dark:hover:border-teal-500'
                      }`}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.65 + index * 0.1 }}
                      whileHover={{ scale: 1.03, y: -3 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <input
                        type="radio"
                        name="billingType"
                        value={opt.value}
                        checked={billingType === opt.value}
                        onChange={() => setBillingType(opt.value)}
                        className="sr-only"
                      />
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-lg font-black text-gray-900 dark:text-white">{opt.label}</span>
                        {billingType === opt.value && (
                          <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: 'spring', stiffness: 200 }}
                          >
                            <IoCheckmarkCircle className="text-teal-500 text-xl" />
                          </motion.div>
                        )}
                      </div>
                    </motion.label>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <motion.div
              className="flex justify-between gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <motion.button
                onClick={() => setOrderStep(1)}
                className="px-8 py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoArrowBack />
                Back
              </motion.button>

              <motion.button
                onClick={() => {
                  if (onComplete) {
                    onComplete(woundDetails);
                  } else {
                    if (ivrSubmitted) {
                      setOrderStep(3);
                    } else {
                      setOpenIvrModal(true);
                    }
                  }
                }}
                disabled={!canProceedWithWoundDetails()}
                className={`px-8 py-4 rounded-2xl font-bold transition-all shadow-lg flex items-center gap-2 ${
                  canProceedWithWoundDetails()
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white hover:shadow-xl'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-60'
                }`}
                whileHover={canProceedWithWoundDetails() ? { scale: 1.02, x: 5 } : {}}
                whileTap={canProceedWithWoundDetails() ? { scale: 0.98 } : {}}
              >
                Next: Insurance Verification
                <IoArrowBack className="rotate-180" />
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default WoundDetailsStep;